/**
 * Reads a trainee's spreadsheet and checks it against a module's expectations.
 *
 * A check is plain data, co-located with the module content the same way the
 * QUIZ arrays are:
 *
 *   { label: 'Invoices tab has the six required headers',
 *     range: 'Invoices!A1:F1',
 *     assert: exactRow(['Invoice','Vendor','Date','Amount','Status','Approver']) }
 *
 * Assertions are deliberately forgiving about presentation and strict about
 * substance — trim, collapse whitespace, case-fold, strip currency symbols,
 * round money. A trainee who is actually right but typed "invoice " with a
 * trailing space must not be told they are wrong, or they stop trusting the
 * checker and start ignoring it.
 */

const SHEETS_API = 'https://sheets.googleapis.com/v4/spreadsheets'

/* ─── Value normalisation ─── */

/** Case-fold, trim, collapse internal whitespace. Used for all text compares. */
export function norm(v) {
  return String(v ?? '').trim().replace(/\s+/g, ' ').toLowerCase()
}

/** "$1,234.50" -> 1234.5, "(500)" -> -500, "" -> NaN */
export function toNumber(v) {
  if (typeof v === 'number') return v
  let s = String(v ?? '').trim()
  if (!s) return NaN
  const negated = /^\(.*\)$/.test(s)
  if (negated) s = s.slice(1, -1)
  s = s.replace(/[$£€,\s]/g, '')
  const n = Number(s)
  if (!Number.isFinite(n)) return NaN
  return negated ? -n : n
}

const isBlank = (v) => v === null || v === undefined || String(v).trim() === ''

/** Drop trailing empty cells — the API omits them inconsistently. */
function trimRow(row) {
  const out = [...(row || [])]
  while (out.length && isBlank(out[out.length - 1])) out.pop()
  return out
}

/** Drop rows that are entirely empty. */
function nonEmptyRows(rows) {
  return (rows || []).filter((r) => trimRow(r).length > 0)
}

/** Flatten a single-column range to a list of non-blank values. */
function columnValues(rows) {
  return (rows || []).map((r) => (r || [])[0]).filter((v) => !isBlank(v))
}

/* ─── Assertions ─── */

/** The first row of the range matches these headers exactly (order matters). */
export function exactRow(expected) {
  return (rows) => {
    const actual = trimRow((rows || [])[0])
    const ok =
      actual.length === expected.length &&
      expected.every((e, i) => norm(e) === norm(actual[i]))
    return {
      ok,
      expected: expected.join(' | '),
      actual: actual.length ? actual.join(' | ') : '(empty)',
      hint: ok
        ? null
        : actual.length !== expected.length
          ? `Expected ${expected.length} columns, found ${actual.length}.`
          : 'The headers are there but not in this order, or one is spelled differently.',
    }
  }
}

/** The range contains exactly n non-empty rows. */
export function rowCount(n) {
  return (rows) => {
    const actual = nonEmptyRows(rows).length
    return {
      ok: actual === n,
      expected: `${n} rows`,
      actual: `${actual} rows`,
      hint: actual === n ? null : actual < n ? 'Some rows are missing.' : 'There are more rows than expected — check for duplicates or a stray header.',
    }
  }
}

/** The range contains at least n non-empty rows. */
export function minRowCount(n) {
  return (rows) => {
    const actual = nonEmptyRows(rows).length
    return { ok: actual >= n, expected: `at least ${n} rows`, actual: `${actual} rows` }
  }
}

/** A single cell equals this text. */
export function cellEquals(expected) {
  return (rows) => {
    const actual = (rows || [])[0]?.[0]
    return {
      ok: norm(actual) === norm(expected),
      expected: String(expected),
      actual: isBlank(actual) ? '(empty)' : String(actual),
    }
  }
}

/** A single cell equals this number, within tolerance. */
export function numberEquals(expected, tolerance = 0.005) {
  return (rows) => {
    const actual = toNumber((rows || [])[0]?.[0])
    return {
      ok: Number.isFinite(actual) && Math.abs(actual - expected) <= tolerance,
      expected: String(expected),
      actual: Number.isFinite(actual) ? String(actual) : '(not a number)',
      hint: Number.isFinite(actual) ? null : 'That cell is empty, or holds text rather than a number.',
    }
  }
}

/** The numbers in a single-column range add up to this, within tolerance. */
export function sumEquals(expected, tolerance = 0.01) {
  return (rows) => {
    const nums = columnValues(rows).map(toNumber).filter(Number.isFinite)
    const actual = nums.reduce((a, b) => a + b, 0)
    return {
      ok: Math.abs(actual - expected) <= tolerance,
      expected: expected.toFixed(2),
      actual: actual.toFixed(2),
      hint: nums.length ? null : 'No numbers found in that column.',
    }
  }
}

/** Every one of these values appears somewhere in a single-column range. */
export function columnContainsAll(expected) {
  return (rows) => {
    const have = new Set(columnValues(rows).map(norm))
    const missing = expected.filter((e) => !have.has(norm(e)))
    return {
      ok: missing.length === 0,
      expected: expected.join(', '),
      actual: missing.length ? `missing: ${missing.join(', ')}` : 'all present',
    }
  }
}

/** The spreadsheet has tabs with these names. Use with `{ sheets: true }`. */
export function hasTabs(expected) {
  return (sheetNames) => {
    const have = new Set((sheetNames || []).map(norm))
    const missing = expected.filter((e) => !have.has(norm(e)))
    return {
      ok: missing.length === 0,
      expected: expected.join(', '),
      actual: (sheetNames || []).join(', ') || '(none)',
      hint: missing.length ? `Missing tab${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}. Tab names are the sheet names along the bottom.` : null,
    }
  }
}

/**
 * The numbers in a single-column range run largest first.
 *
 * Blanks are skipped, but a cell holding text fails rather than being ignored:
 * a column with "45,000" typed as text is not sorted by value, it only looks
 * it, and the trainee needs to know that now rather than in module 22 when a
 * script reads it.
 */
export function columnDescending() {
  return (rows) => {
    const raw = columnValues(rows)
    const nums = raw.map(toNumber)

    if (raw.length < 2) {
      return {
        ok: false,
        expected: 'largest first',
        actual: `${raw.length} value${raw.length === 1 ? '' : 's'} found`,
        hint: 'There is almost nothing in that column — check the data is still there.',
      }
    }

    const notNumber = nums.findIndex((n) => !Number.isFinite(n))
    if (notNumber !== -1) {
      return {
        ok: false,
        expected: 'numbers, largest first',
        actual: `"${raw[notNumber]}" is not a number`,
        hint: 'One of those cells holds text, so the column was never really sorted by value.',
      }
    }

    // 1e-9 so a float tail from a formula doesn't read as out of order.
    const broken = nums.findIndex((n, i) => i > 0 && n > nums[i - 1] + 1e-9)
    return {
      ok: broken === -1,
      expected: 'largest first',
      actual: broken === -1 ? 'largest first' : `${nums[broken]} sits below ${nums[broken - 1]}`,
      hint: broken === -1
        ? null
        : 'Those rows are not in order, largest to smallest. Ask again, and name the column to sort on.',
    }
  }
}

/**
 * A two-column range read as label -> number: every expected label must be
 * present, once, with the expected value beside it.
 *
 * Row order is not compared, and unexpected rows are ignored, because a
 * summary the trainee built by asking can legitimately come out in any order
 * and may carry a grand-total line. What it cannot do is get a number wrong.
 */
export function pairsEqual(expected, tolerance = 0.01) {
  const wanted = Object.entries(expected)
  return (rows) => {
    const found = new Map()
    ;(rows || []).forEach((r) => {
      const key = norm((r || [])[0])
      if (!key || found.has(key)) return
      found.set(key, toNumber((r || [])[1]))
    })

    const problems = []
    wanted.forEach(([label, value]) => {
      const actual = found.get(norm(label))
      if (actual === undefined) {
        problems.push(`${label} is missing`)
      } else if (!Number.isFinite(actual)) {
        problems.push(`${label} has no number beside it`)
      } else if (Math.abs(actual - value) > tolerance) {
        problems.push(`${label} shows ${actual.toFixed(2)}, expected ${value.toFixed(2)}`)
      }
    })

    return {
      ok: problems.length === 0,
      expected: wanted.map(([l, v]) => `${l} ${v.toFixed(2)}`).join(', '),
      actual: problems.length ? problems.join('; ') : 'all correct',
      hint: problems.length
        ? 'Check the labels are spelled the same as the Status column, and that each total covers only that status.'
        : null,
    }
  }
}

/**
 * Every value in a single-column range is a number, optionally within bounds.
 *
 * Checks like this one exist because the trainee's data is now generated rather
 * than pasted: we cannot assert what the amounts ARE, only that they are
 * amounts. Strict on substance — text in a money column is a real defect that
 * breaks every later exercise — and silent about the values themselves.
 */
export function columnAllNumeric({ min = null, max = null } = {}) {
  return (rows) => {
    const raw = columnValues(rows)
    if (!raw.length) {
      return { ok: false, expected: 'numbers', actual: '(nothing in that column)' }
    }
    const bad = raw.find((v) => {
      const n = toNumber(v)
      if (!Number.isFinite(n)) return true
      if (min !== null && n < min) return true
      if (max !== null && n > max) return true
      return false
    })
    const bounds = [min !== null ? `at least ${min}` : null, max !== null ? `at most ${max}` : null]
      .filter(Boolean).join(' and ')
    return {
      ok: bad === undefined,
      expected: `${raw.length} numbers${bounds ? `, ${bounds}` : ''}`,
      actual: bad === undefined ? 'all numeric' : `"${bad}" is not a usable number`,
      hint: bad === undefined ? null : 'One cell holds text, a blank, or a value outside the range asked for.',
    }
  }
}

/**
 * Every cell in a range was produced by this function rather than typed.
 *
 * Use with `{ formula: true }`, which fetches what is IN the cells instead of
 * what they evaluate to. Checking the values alone cannot tell a custom
 * function from a person typing the right answers, and the whole point of the
 * exercise is that a function did it.
 */
export function everyCellCalls(functionName) {
  const wanted = functionName.toLowerCase()
  return (rows) => {
    const cells = columnValues(rows)
    if (!cells.length) {
      return { ok: false, expected: `=${functionName}(...) in every cell`, actual: '(the column is empty)' }
    }
    const typed = cells.filter((c) => !String(c).toLowerCase().includes(`${wanted}(`))
    return {
      ok: typed.length === 0,
      expected: `=${functionName}(...) in all ${cells.length} cells`,
      actual: typed.length
        ? `${typed.length} cell${typed.length === 1 ? ' holds' : 's hold'} a value rather than the function — e.g. "${typed[0]}"`
        : `all ${cells.length} call ${functionName}`,
      hint: typed.length
        ? `Those cells contain an answer, not a call to ${functionName}. Typing the result is the thing this exercise is asking you not to do.`
        : null,
    }
  }
}

/**
 * Every cell in a range holds a formula rather than a typed value.
 *
 * Use with `{ formula: true }`. A script that computes an answer and writes the
 * number produces a sheet that looks right and stops being right the moment
 * anything it depended on changes — so "did you leave a formula behind" is a
 * real question about the work, not a stylistic one.
 */
export function everyCellIsFormula() {
  return (rows) => {
    const cells = columnValues(rows)
    if (!cells.length) {
      return { ok: false, expected: 'a formula in every cell', actual: '(the column is empty)' }
    }
    const literal = cells.filter((c) => !String(c).trim().startsWith('='))
    return {
      ok: literal.length === 0,
      expected: `a formula in all ${cells.length} cells`,
      actual: literal.length
        ? `${literal.length} cell${literal.length === 1 ? ' holds' : 's hold'} a fixed value — e.g. "${literal[0]}"`
        : `all ${cells.length} are formulas`,
      hint: literal.length
        ? 'Those cells hold an answer rather than the sum that produced it, so they will not update when the numbers behind them change.'
        : null,
    }
  }
}

/** Every value in a single-column range is one of these. */
export function columnValuesFrom(allowed) {
  return (rows) => {
    const raw = columnValues(rows)
    const permitted = new Set(allowed.map(norm))
    const stray = [...new Set(raw.filter((v) => !permitted.has(norm(v))))]
    return {
      ok: raw.length > 0 && stray.length === 0,
      expected: allowed.join(', '),
      actual: raw.length === 0 ? '(empty column)' : stray.length ? `also found: ${stray.slice(0, 3).join(', ')}` : 'all recognised',
      hint: stray.length ? 'Those values were not in the list the prompt asked for.' : null,
    }
  }
}

/** No value repeats in a single-column range — invoice numbers, ids. */
export function columnUnique() {
  return (rows) => {
    const raw = columnValues(rows).map(norm)
    const seen = new Set()
    const dupes = new Set()
    raw.forEach((v) => (seen.has(v) ? dupes.add(v) : seen.add(v)))
    return {
      ok: raw.length > 0 && dupes.size === 0,
      expected: `${raw.length} distinct values`,
      actual: dupes.size ? `repeated: ${[...dupes].slice(0, 3).join(', ')}` : 'all distinct',
      hint: dupes.size ? 'Two rows share a value that is meant to identify one row.' : null,
    }
  }
}

/**
 * A grouped summary agrees with the rows it summarises.
 *
 * Takes TWO ranges — the source rows as [key, value] and the summary as
 * [key, total] — and recomputes the totals rather than comparing against a
 * fixture. This is what makes checking possible once the data is generated
 * per trainee: the question stops being "does your data match ours" and becomes
 * "does your summary match your own data", which is the thing actually worth
 * getting right.
 */
export function summaryMatchesSource({ tolerance = 0.02, keyIndex = 0, valueIndex = 1 } = {}) {
  return ([sourceRows, summaryRows]) => {
    const totals = new Map()
    ;(sourceRows || []).forEach((r) => {
      // The grouping column is not always to the left of the value column —
      // in the register, Amount is D and Status is E — so which is which is
      // stated by the caller rather than assumed from position.
      const key = norm((r || [])[keyIndex])
      const value = toNumber((r || [])[valueIndex])
      if (!key || !Number.isFinite(value)) return
      totals.set(key, (totals.get(key) || 0) + value)
    })

    if (!totals.size) {
      return { ok: false, expected: 'a summary of the rows', actual: 'the source rows could not be read' }
    }

    const shown = new Map()
    ;(summaryRows || []).forEach((r) => {
      const key = norm((r || [])[0])
      if (!key || shown.has(key)) return
      shown.set(key, toNumber((r || [])[1]))
    })

    const problems = []
    totals.forEach((want, key) => {
      const got = shown.get(key)
      if (got === undefined) problems.push(`${key} is missing`)
      else if (!Number.isFinite(got)) problems.push(`${key} has no number beside it`)
      else if (Math.abs(got - want) > tolerance) {
        problems.push(`${key} shows ${got.toFixed(2)}, but the rows add up to ${want.toFixed(2)}`)
      }
    })

    return {
      ok: problems.length === 0,
      expected: `${totals.size} group${totals.size === 1 ? '' : 's'}, each totalling what the rows say`,
      actual: problems.length ? problems.slice(0, 3).join('; ') : 'every total agrees with the rows',
      hint: problems.length ? 'The summary and the register disagree — recompute it from the rows as they stand now.' : null,
    }
  }
}

/* ─── Spreadsheet id parsing ─── */

/** The only host a submitted spreadsheet link may live on. */
export const SHEETS_HOST = 'docs.google.com'

/**
 * Accepts a full Google Sheets URL or a bare id. Returns null if it can't
 * find something that looks like an id.
 *
 * The host and scheme are checked, not just the path. An earlier version
 * searched for the substring `/spreadsheets/d/<id>` anywhere in the input,
 * which accepted `https://evil.example.com/spreadsheets/d/AAAA…` and
 * `javascript:…//spreadsheets/d/AAAA` — and what a trainee submits here is
 * later shown to an admin. Anything a trainee types that ends up in an href
 * is a phishing vector aimed at the most privileged users of the app, so the
 * id is parsed out and the URL is rebuilt by `sheetUrlFor` rather than
 * echoed back.
 */
export function parseSpreadsheetId(input) {
  const s = String(input || '').trim()
  if (!s) return null

  // A bare id: Google's are long and have no slashes, spaces or dots, so this
  // can never match a URL.
  if (/^[a-zA-Z0-9-_]{20,}$/.test(s)) return s

  let url
  try {
    url = new URL(s)
  } catch {
    return null
  }
  if (url.protocol !== 'https:') return null
  // Exact host: a suffix test would accept docs.google.com.evil.io.
  if (url.hostname !== SHEETS_HOST) return null

  // Both /spreadsheets/d/<id> and the multi-account /spreadsheets/u/0/d/<id>.
  const m = url.pathname.match(/^\/spreadsheets(?:\/u\/\d+)?\/d\/([a-zA-Z0-9-_]+)/)
  return m ? m[1] : null
}

/**
 * The canonical URL for a spreadsheet id — the only form that should ever be
 * stored or rendered as a link. Returns null for anything that isn't an id,
 * so a caller cannot accidentally build an href out of arbitrary text.
 */
export function sheetUrlFor(spreadsheetId) {
  const id = String(spreadsheetId || '').trim()
  if (!/^[a-zA-Z0-9-_]{10,}$/.test(id)) return null
  return `https://${SHEETS_HOST}/spreadsheets/d/${id}/edit`
}

/* ─── API access ─── */

class SheetsError extends Error {
  constructor(message, code) {
    super(message)
    this.code = code
  }
}

async function apiGet(url, token) {
  let res
  try {
    res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
  } catch {
    throw new SheetsError('Could not reach Google. Check your connection and try again.', 'network')
  }

  if (res.ok) return res.json()

  if (res.status === 401) {
    throw new SheetsError('Your access to Google expired.', 'auth')
  }
  if (res.status === 403) {
    throw new SheetsError(
      "You don't have access to that spreadsheet. Make sure the link is to a sheet you own.",
      'forbidden'
    )
  }
  if (res.status === 404) {
    throw new SheetsError(
      "We couldn't find a spreadsheet at that link. Copy the URL straight from your browser's address bar.",
      'not-found'
    )
  }
  throw new SheetsError(`Google returned an error (${res.status}).`, 'api')
}

/** Tab names for a spreadsheet. Also proves the sheet exists and is readable. */
async function fetchSheetNames(spreadsheetId, token) {
  const url = `${SHEETS_API}/${encodeURIComponent(spreadsheetId)}?fields=sheets.properties.title`
  const data = await apiGet(url, token)
  return (data.sheets || []).map((s) => s.properties?.title).filter(Boolean)
}

/**
 * @param {'UNFORMATTED_VALUE'|'FORMULA'} render
 *   FORMULA returns what was typed into the cell rather than what it evaluates
 *   to, which is the only way to tell a custom function apart from someone
 *   typing the answer. Values remain the default: for everything else, what
 *   the sheet SAYS is what matters, not how it got there.
 */
async function fetchRanges(spreadsheetId, ranges, token, render = 'UNFORMATTED_VALUE') {
  if (!ranges.length) return {}
  const qs = ranges.map((r) => `ranges=${encodeURIComponent(r)}`).join('&')
  const url =
    `${SHEETS_API}/${encodeURIComponent(spreadsheetId)}/values:batchGet?${qs}` +
    `&valueRenderOption=${render}&dateTimeRenderOption=FORMATTED_STRING`
  const data = await apiGet(url, token)
  const out = {}
  ;(data.valueRanges || []).forEach((vr, i) => {
    out[ranges[i]] = vr.values || []
  })
  return out
}

/** The tab part of "Invoices!A1:F1". Null for an unqualified range. */
function sheetNameOf(range) {
  const m = String(range).match(/^'?(.*?)'?!/)
  return m ? m[1] : null
}

/* ─── The entry point ─── */

/**
 * Run a module's checks against a spreadsheet.
 *
 * Returns `{ passed, score, results }` where score is the fraction of checks
 * that passed. Throws a SheetsError (with `.code`) if the spreadsheet can't be
 * read at all — the caller re-prompts for consent on code 'auth'.
 */
export async function runChecks(spreadsheetId, checks, token) {
  const sheetNames = await fetchSheetNames(spreadsheetId, token)
  const known = new Set(sheetNames.map(norm))

  // Only ask for ranges whose tab actually exists. Sending a range for a
  // missing tab makes the whole batch 400, which would turn one wrong tab name
  // into "everything failed" with no usable explanation.
  const rangesOf = (c) => c.ranges || (c.range ? [c.range] : [])
  const servable = (r) => {
    const tab = sheetNameOf(r)
    return !tab || known.has(norm(tab))
  }

  const uniqueRanges = [...new Set(checks.filter((c) => !c.formula).flatMap(rangesOf).filter(servable))]
  // Formula checks need a second call: one batchGet carries one render mode.
  const formulaRanges = [...new Set(checks.filter((c) => c.formula).flatMap(rangesOf).filter(servable))]

  let valuesByRange = {}
  let formulasByRange = {}
  if (uniqueRanges.length) {
    valuesByRange = await fetchRanges(spreadsheetId, uniqueRanges, token)
  }
  if (formulaRanges.length) {
    formulasByRange = await fetchRanges(spreadsheetId, formulaRanges, token, 'FORMULA')
  }

  const results = checks.map((check) => {
    const base = { label: check.label }
    try {
      if (check.sheets) {
        return { ...base, ...check.assert(sheetNames) }
      }
      // A check may read several ranges at once — a summary compared against
      // the rows it summarises needs both, and neither is worth checking
      // without the other.
      const needed = check.ranges || [check.range]
      const missingTab = needed
        .map((r) => sheetNameOf(r))
        .find((tab) => tab && !known.has(norm(tab)))
      if (missingTab) {
        return {
          ...base,
          ok: false,
          expected: `a tab named "${missingTab}"`,
          actual: sheetNames.join(', ') || '(none)',
          hint: `There's no tab called "${missingTab}". Tab names are along the bottom of the spreadsheet — rename one, or ask Claude to.`,
        }
      }
      const source = check.formula ? formulasByRange : valuesByRange
      const values = needed.map((r) => source[r] || [])
      return { ...base, ...check.assert(check.ranges ? values : values[0]) }
    } catch (err) {
      // A broken assertion is our bug, not the trainee's — say so rather than
      // failing them for it.
      return {
        ...base,
        ok: false,
        expected: '(check could not run)',
        actual: err?.message || 'unknown error',
        hint: 'This check itself is broken. Tell your trainer.',
      }
    }
  })

  const passedCount = results.filter((r) => r.ok).length
  return {
    passed: passedCount === results.length && results.length > 0,
    score: results.length ? passedCount / results.length : 0,
    results,
  }
}
